import React, { useState, useEffect, useCallback } from 'react';

import { useNavigate } from 'react-router-dom';
import '../Marcas/Marcas.css'; // Estilos padronizados
import MenuLateral from "../Components/MenuPrincipal/MenuLateral";
import Pagination from '../Components/Pagination/Pagination';
import { useAuth } from '../Context/auth';
import Swal from 'sweetalert2';

// Função auxiliar de formatação
const formatarData = (isoString, includeTime = false) => {
    if (!isoString) return '—';
    const data = new Date(isoString);
    if (isNaN(data.getTime())) return '—'; // Validação adicional
    const options = {
        day: '2-digit', month: '2-digit', year: 'numeric',
        timeZone: 'America/Sao_Paulo'
    };
    if (includeTime) {
        options.hour = '2-digit';
        options.minute = '2-digit';
    }
    return new Intl.DateTimeFormat('pt-BR', options).format(data);
};

function Movimentacoes() {
    const { currentUser, appUserData } = useAuth();
    const navigate = useNavigate();
    const [movimentacoes, setMovimentacoes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [erro, setErro] = useState('');

    // Estados de Filtro e Busca
    const [pesquisa, setPesquisa] = useState('');
    const [textoPesquisa, setTextoPesquisa] = useState('');
    const [dataInicio, setDataInicio] = useState('');
    const [dataFim, setDataFim] = useState('');

    // Estados de Paginação
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(20);

    // --- Debounce da Pesquisa ---
    useEffect(() => {
        const timerId = setTimeout(() => {
            setTextoPesquisa(pesquisa);
            setCurrentPage(1); // Resetar página ao pesquisar
        }, 500);
        return () => clearTimeout(timerId);
    }, [pesquisa]);

    // Verifica permissão de leitura ao carregar
    useEffect(() => {
        if (appUserData && !appUserData['ler_movimentacoes']) {
            Swal.fire({
                icon: 'error',
                title: 'Acesso Negado',
                text: 'Você não tem permissão para visualizar esta tela.',
                confirmButtonColor: '#2185c6'
            }).then(() => {
                navigate('/app/home');
            });
        }
    }, [appUserData, navigate]);

    // --- Busca de Dados ---
    const fetchMovimentacoes = useCallback(async () => {
        if (!currentUser) return;
        setLoading(true);
        try {
            const token = await currentUser.getIdToken();
            const API_URL = process.env.REACT_APP_API_URL;
            const response = await fetch(`${API_URL}/api/movimentacoes`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });

            if (!response.ok) {
                if (response.status === 401 || response.status === 403) {
                    throw new Error("Não autorizado. Verifique sua sessão.");
                }
                throw new Error("Erro ao buscar movimentações.");
            }

            const data = await response.json();
            setMovimentacoes(data || []);

        } catch (error) {
            console.error("Erro no fetchMovimentacoes:", error);
            setErro(error.message);
        } finally {
            setLoading(false);
        }
    }, [currentUser]);

    useEffect(() => {
        fetchMovimentacoes();
    }, [fetchMovimentacoes]);

    // --- Exclusão ---
    const handleDelete = async (id) => {
        if (!appUserData || !appUserData['excluir_movimentacoes']) {
            Swal.fire({
                icon: 'error',
                title: 'Acesso Negado',
                text: 'Você não tem permissão para excluir movimentações.',
                confirmButtonColor: '#2185c6'
            });
            return;
        }
        const result = await Swal.fire({
            title: 'Você tem certeza?',
            text: "Esta ação reverterá o impacto no estoque e não poderá ser desfeita!",
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            cancelButtonColor: '#3085d6',
            confirmButtonText: 'Sim, excluir!',
            cancelButtonText: 'Cancelar'
        });

        if (result.isConfirmed) {
            try {
                const token = await currentUser.getIdToken();
                const API_URL = process.env.REACT_APP_API_URL;
                const response = await fetch(`${API_URL}/api/movimentacoes/${id}`, {
                    method: 'DELETE',
                    headers: { 'Authorization': `Bearer ${token}` }
                });

                if (!response.ok) throw new Error('Falha ao excluir movimentação.');

                setMovimentacoes(prev => prev.filter(mov => mov.id !== id));
                Swal.fire('Excluído!', 'A movimentação foi excluída com sucesso.', 'success');
            } catch (error) {
                Swal.fire('Erro', error.message, 'error');
            }
        }
    };

    // --- Filtragem ---
    const filteredMovimentacoes = movimentacoes.filter(mov => {
        // Filtro por Texto (Descrição, Origem, Documento)
        const desc = (mov.descricao || '').toLowerCase();
        const origem = (mov.origem || '').toLowerCase();
        const doc = (mov.documento || '').toLowerCase();
        const search = textoPesquisa.toLowerCase();

        const matchText = desc.includes(search) || origem.includes(search) || doc.includes(search);

        // Filtro por Data
        let matchDate = true;
        if (dataInicio || dataFim) {
            const movDate = new Date(mov.data_movimentacao).toISOString().split('T')[0];
            if (dataInicio && movDate < dataInicio) matchDate = false;
            if (dataFim && movDate > dataFim) matchDate = false;
        }

        return matchText && matchDate;
    });

    // --- Paginação ---
    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    const currentMovimentacoes = filteredMovimentacoes.slice(indexOfFirstItem, indexOfLastItem);

    const handlePageChange = (pageNumber) => setCurrentPage(pageNumber);
    const handleItemsPerPageChange = (newLimit) => {
        setItemsPerPage(newLimit);
        setCurrentPage(1);
    };

    return (
        <div className="marcas-page-container" style={{ display: 'flex', paddingTop: 0 }}>
            <MenuLateral />

            <div className="content-with-sidebar">
                <div className="marcas-header">
                    <h1 className="marcas-title">
                        <i className="fas fa-exchange-alt text-primary"></i>
                        Movimentações de <span className="highlight">Estoque</span>
                    </h1>
                </div>

                <main className="marcas-content" style={{ maxWidth: '98%' }}>
                    <div className="marcas-controls">
                        <button
                            onClick={() => {
                                if (!appUserData || !appUserData['cadastrar_movimentacoes']) {
                                    Swal.fire({
                                        icon: 'error',
                                        title: 'Acesso Negado',
                                        text: 'Você não tem permissão para cadastrar movimentações.',
                                        confirmButtonColor: '#2185c6'
                                    });
                                    return;
                                }
                                navigate('/app/novamovimentacao');
                            }}
                            className="btn-primary-custom me-2"
                        >
                            <i className="fas fa-plus"></i>Nova Movimentação
                        </button>
                        <div className="search-wrapper">
                            <i className="fas fa-search search-icon"></i>
                            <input
                                type="text"
                                className="search-input-custom"
                                placeholder="Pesquisar por descrição, origem ou documento..."
                                value={pesquisa}
                                onChange={(e) => setPesquisa(e.target.value)}
                            />
                        </div>

                        <div className="d-flex gap-2 align-items-center">
                            <input
                                type="date"
                                className="form-control form-control-sm"
                                value={dataInicio}
                                onChange={e => setDataInicio(e.target.value)}
                                title="Data Inicial"
                                style={{ maxWidth: '140px' }}
                            />
                            <span className="text-muted">-</span>
                            <input
                                type="date"
                                className="form-control form-control-sm"
                                value={dataFim}
                                onChange={e => setDataFim(e.target.value)}
                                title="Data Final"
                                style={{ maxWidth: '140px' }}
                            />
                            {(dataInicio || dataFim) && (
                                <button className="btn-icon-custom" style={{ width: '32px', height: '32px' }} onClick={() => { setDataInicio(''); setDataFim(''); }} title="Limpar datas">
                                    <i className="fas fa-times"></i>
                                </button>
                            )}
                        </div>
                    </div>

                    {erro && (
                        <div className="alert alert-danger d-flex align-items-center mb-4">
                            <i className="fas fa-exclamation-triangle me-2"></i>
                            <div>{erro}</div>
                        </div>
                    )}

                    {loading ? (
                        <div className="text-center p-5">
                            <div className="spinner-border text-primary" role="status">
                                <span className="visually-hidden">Carregando...</span>
                            </div>
                        </div>
                    ) : (
                        <>
                            <div className="marcas-grid-container">
                                <table className="table-custom">
                                    <thead>
                                        <tr>
                                            <th style={{ width: '8%', textAlign: 'center' }}>Ações</th>
                                            <th style={{ width: '10%' }}>Data Mov.</th>
                                            <th style={{ width: '8%' }}>Tipo</th>
                                            <th style={{ width: '20%' }}>Descrição</th>
                                            <th style={{ width: '10%' }}>Origem</th>
                                            <th style={{ width: '10%' }}>Documento</th>
                                            <th style={{ width: '8%', textAlign: 'center' }}>Qtde. Total</th>
                                            <th style={{ width: '10%' }}>Data Cad.</th>
                                            <th style={{ width: '10%' }}>Usuário Cad.</th>
                                            <th style={{ width: '10%' }}>Data Alt.</th>
                                            <th style={{ width: '10%' }}>Usuário Alt.</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {currentMovimentacoes.map(mov => (
                                            <tr key={mov.id}>
                                                <td className="actions-cell" data-label="Ações">
                                                    <button
                                                        onClick={() => {
                                                            if (!appUserData || !appUserData['editar_movimentacoes']) {
                                                                Swal.fire({
                                                                    icon: 'error',
                                                                    title: 'Acesso Negado',
                                                                    text: 'Você não tem permissão para editar movimentações.',
                                                                    confirmButtonColor: '#2185c6'
                                                                });
                                                                return;
                                                            }
                                                            navigate(`/app/editarmovimentacao/${mov.id}`);
                                                        }}
                                                        className="btn-action-sm btn-edit"
                                                        title="Editar"
                                                    >
                                                        <i className="fas fa-pen"></i>
                                                    </button>
                                                    <button onClick={() => handleDelete(mov.id)} className="btn-action-sm btn-delete ms-2" title="Excluir">
                                                        <i className="fas fa-trash"></i>
                                                    </button>
                                                </td>
                                                <td data-label="Data Mov.">{formatarData(mov.data_movimentacao)}</td>
                                                <td data-label="Tipo">
                                                    <span className={`badge ${mov.tipo === 'entrada' ? 'bg-success' : 'bg-danger'}`}>
                                                        {mov.tipo ? mov.tipo.toUpperCase() : '-'}
                                                    </span>
                                                </td>
                                                <td data-label="Descrição">{mov.descricao || '—'}</td>
                                                <td data-label="Origem">{mov.origem || '-'}</td>
                                                <td data-label="Documento">{mov.documento || '-'}</td>
                                                <td style={{ textAlign: 'center' }} data-label="Qtde. Total">{mov.quantidade_total !== null ? mov.quantidade_total : '-'}</td>
                                                <td data-label="Data Cad.">{formatarData(mov.data_cadastro, true)}</td>
                                                <td data-label="Usuário Cad.">{mov.usuario_cadastro_nome || '-'}</td>
                                                <td data-label="Data Alt.">{formatarData(mov.data_ultima_alteracao, true)}</td>
                                                <td data-label="Usuário Alt.">{mov.usuario_alteracao_nome || '-'}</td>
                                            </tr>
                                        ))}
                                        {currentMovimentacoes.length === 0 && (
                                            <tr>
                                                <td colSpan="11" className="text-center py-4 text-muted">
                                                    Nenhuma movimentação encontrada.
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>

                            <Pagination
                                totalItems={filteredMovimentacoes.length}
                                itemsPerPage={itemsPerPage}
                                currentPage={currentPage}
                                onPageChange={handlePageChange}
                                onItemsPerPageChange={handleItemsPerPageChange}
                            />
                        </>
                    )}
                </main>
            </div>
        </div>
    );
}

export default Movimentacoes;
